import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { compact, find, isEmpty, isNil, map, values } from 'lodash';

import { filterOrderLineItems } from 'store/thunks/workQueueThunks';

import OrderStatus from 'common/constants/orderStatus';
import { casingFormat, formatStateFilterLabel } from 'common/utils';
import { SortKey } from 'enums/common.enum';
import orderFillType from 'common/constants/orderFillType';
import FacilityFilterStatusChip from './FacilityFilterStatusChip';

const getDateRange = (dateRange) => {
  const dateArray = Array.isArray(dateRange) ? dateRange : [dateRange];
  const dates = map(dateArray, (date) => {
    const [year, month, day] = date.split('-').map(Number);
    return format(new Date(year, month - 1, day), 'MM/dd/yyyy');
  });

  return dates.length === 1 ? `${dates[0]}` : `${dates[0]} - ${dates[1]}`;
};

const OrderLineItemsFilterStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.orderLineItems?.filter);
  const selectedDrugProducts = useSelector(
    ({ workQueue }) => workQueue.orderLineItems?.filter?.selectedDrugProducts || []
  );
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const removeFilter = (filterName) => () => {
    dispatch(
      filterOrderLineItems({
        filter: {
          ...filter,
          [filterName]: undefined,
          // Match the Bulk queue: the filter panel reads this back on open, and `undefined`
          // would leave the last selection in place instead of clearing the chip.
          ...(filterName === 'selectedDrugProducts' && { selectedDrugProducts: [] }),
          ...(filterName === 'createdAt' && { createdBetween: undefined }),
          ...(filterName === 'createdBetween' && { createdAt: undefined }),
        },
      })
    );
  };

  const removeAllFilter = () => dispatch(filterOrderLineItems({ filter: {} }));

  return (
    <Stack
      direction='row'
      spacing={1}
      flexWrap='wrap'
      rowGap={1}
      alignItems='center'
      sx={{ maxWidth: '80rem' }}
    >
      <FacilityFilterStatusChip
        removeFilter={removeFilter}
        selectedFacilities={filter?.fulfillingPharmacyIds || []}
      />

      {filter.orderStatus && (
        <Chip
          label={`Status = ${OrderStatus[filter.orderStatus]}`}
          variant='contained'
          onDelete={removeFilter(SortKey.ORDER_STATUS)}
        />
      )}

      {filter.patientName && (
        <Chip
          label={`Name = ${filter.patientName}`}
          variant='contained'
          onDelete={removeFilter(SortKey.PATIENT_NAME)}
        />
      )}

      {filter.orderId && (
        <Chip
          label={`Order Id = ${filter.orderId}`}
          variant='contained'
          onDelete={removeFilter(SortKey.ORDER_ID)}
        />
      )}
      {filter.partnerAccount && (
        <Chip
          label={`Partner Account = ${filter.partnerAccount}`}
          variant='contained'
          onDelete={removeFilter(SortKey.PARTNER_ACCOUNT)}
        />
      )}

      {filter.itemName && (
        <Chip
          label={`Drug Name = ${filter.itemName}`}
          variant='contained'
          onDelete={removeFilter(SortKey.ITEM_NAME)}
        />
      )}

      {!isEmpty(selectedDrugProducts) && (
        <Chip
          key='drug-products'
          label={
            <Tooltip title={selectedDrugProducts.map((product) => product.label).join(', ')}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatStateFilterLabel(
                  selectedDrugProducts.map((product) => product.label),
                  'Drug Name'
                )}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('selectedDrugProducts')}
        />
      )}

      {!isEmpty(filter.ndcs) && (
        <Chip
          key='ndcs'
          label={
            <Tooltip title={filter.ndcs.join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.ndcs, 'NDC')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('ndcs')}
        />
      )}

      {filter.fillType && (
        <Chip
          label={`Fill Type = ${orderFillType[filter.fillType]}`}
          variant='contained'
          onDelete={removeFilter(SortKey.FILL_TYPE)}
        />
      )}

      {filter.partnerName && (
        <Chip
          label={`Partner Name = ${
            find(partners, { partnerName: filter.partnerName }).partnerName
          }`}
          variant='contained'
          onDelete={removeFilter(SortKey.PARTNER_NAME)}
        />
      )}

      {filter.requestedDate && (
        <Chip
          label={`Requested Date = ${format(parseISO(filter.requestedDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter(SortKey.REQUESTED_DATE)}
        />
      )}

      {filter.shipByDate && (
        <Chip
          label={`Ship By Date = ${format(parseISO(filter.shipByDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter(SortKey.SHIP_BY_DATE)}
        />
      )}

      {!isNil(filter.isReshipment) && (
        <Chip
          label={`Reshipments = ${filter.isReshipment ? 'Yes' : 'No'}`}
          variant='contained'
          onDelete={removeFilter('isReshipment')}
        />
      )}

      {filter?.partnerIds && (
        <Chip
          label={
            <Tooltip
              title={`Partner = ${filter?.partnerIds
                .map((partnerId) => {
                  const partner = partners?.find((p) => p?.partnerUuid === partnerId);
                  return casingFormat(partner?.partnerName || '');
                })
                .join(', ')}`}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(
                  filter?.partnerIds?.map((partnerId) => {
                    const partner = partners?.find((p) => p?.partnerUuid === partnerId);
                    return casingFormat(partner?.partnerName || '');
                  }),
                  'Partner '
                )}
              </Box>
            </Tooltip>
          }
          key='partners'
          variant='contained'
          onDelete={removeFilter('partnerIds')}
        />
      )}

      {filter?.createdAt && (
        <Chip
          label={`Date = ${getDateRange(filter.createdAt)}`}
          variant='contained'
          onDelete={removeFilter('createdAt')}
        />
      )}

      {filter?.createdBetween && (
        <Chip
          label={`Date Range = ${getDateRange(filter.createdBetween)}`}
          variant='contained'
          onDelete={removeFilter('createdBetween')}
        />
      )}

      {!isEmpty(compact(values(filter))) && <Button onClick={removeAllFilter}>Remove All</Button>}
    </Stack>
  );
};

export default OrderLineItemsFilterStatusBar;
